import React, { useState, useEffect, useCallback } from 'react'
import { opcionesDeporte } from '../../../lib/deportesOrg'
import { Plus, Search, Filter, ChevronDown, ChevronUp, Trash2, User, Users, Package } from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Badge, Card, CardContent, Input, Label, Combobox, ChipFilter,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../../ui'
import {
  getMatrizEntrega, asignarPrenda, asignarPrendaMasivo, actualizarAsignacion, eliminarAsignacion,
  getAllGroups, getOrgDeportes,
} from '../../../lib/supabase'
import { useTemporadaActiva } from '../pagos/useTemporadaActiva'
import { useDeportes } from '../../../hooks/useDeportes'
import { etiquetaTemporada } from '../../../lib/uniformesMultiTemporada'
import { useOrganization } from '../../../contexts/OrganizationContext'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'
import { useInscritosTemporada } from '../../../hooks/useInscritosTemporada'
import { FiltroInscritos, AvisoSinPadron } from '../FiltroInscritos'
import { formatMoney } from '../../../utils/helpers'

const ENTREGA_BADGE = {
  no_entregado: { label: 'No entregado', variant: 'secondary' },
  pendiente: { label: 'Pendiente', variant: 'warning' },
  entregado: { label: 'Entregado', variant: 'success' },
}

const ExtrasPage = () => {
  const { temporadaId, temporadas, setTemporadaId } = useTemporadaActiva()
  const { label: deporteLabel } = useDeportes()

  // El mismo control de temporada que las demas pantallas del modulo (ux-rules §13).
  const chipsTemporada = (temporadas || [])
    .filter((t) => t.estado === 'abierta' || t.id === temporadaId)
    .map((t) => ({ value: t.id, label: etiquetaTemporada(t, deporteLabel) }))
  const inscritosEstado = useInscritosTemporada(temporadaId)
  const { org } = useOrganization()
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const [orgDeportes, setOrgDeportes] = useState([])
  const [matriz, setMatriz] = useState({ prendas: [], jugadores: [], extras: [] })
  const [grupos, setGrupos] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [deporteFilter, setDeporteFilter] = useState('todos')
  const [grupoFilter, setGrupoFilter] = useState('todos')
  const [pagadoFilter, setPagadoFilter] = useState('todos')
  const [entregaFilter, setEntregaFilter] = useState('todos')

  // Modal: asignar prenda extra
  const [assignOpen, setAssignOpen] = useState(false)
  const assignGuard = useUnsavedGuard({ onClose: () => setAssignOpen(false) })
  const [assignForm, setAssignForm] = useState({ prendaId: '', tipo: 'jugador', jugadorId: '', grupoId: '', talla: '', cantidad: 1 })
  const [assignSaving, setAssignSaving] = useState(false)
  // Modal: editar una extra existente
  const [editModal, setEditModal] = useState({ open: false, row: null })
  const editGuard = useUnsavedGuard({ onClose: () => setEditModal({ open: false, row: null }) })
  const [editForm, setEditForm] = useState({ talla: '', estatus: 'no_entregado' })
  const [editSaving, setEditSaving] = useState(false)

  const load = useCallback(async () => {
    if (!temporadaId) return
    setLoading(true)
    const [matrizRes, gruposRes, deportesRes] = await Promise.all([
      getMatrizEntrega(temporadaId, {}),
      getAllGroups(),
      getOrgDeportes(org?.id),
    ])
    if (matrizRes.data) setMatriz(matrizRes.data)
    setGrupos((gruposRes.data || []).filter((g) => g.estatus === 'Activo'))
    setOrgDeportes(deportesRes.data || [])
    setLoading(false)
  }, [temporadaId, org?.id])

  useEffect(() => { load() }, [load])

  const deporteOptions = [
    { value: 'todos', label: 'Todos' },
    ...opcionesDeporte(orgDeportes),
  ]
  const jugadorById = Object.fromEntries((matriz.jugadores || []).map((j) => [j.id, j]))
  const prendaById = Object.fromEntries((matriz.prendas || []).map((p) => [p.id, p]))

  // Asignar un extra genera un CARGO, asi que la lista de a quien se le puede
  // asignar tiene que respetar el padron de la temporada.
  const jugadoresAsignables = inscritosEstado.filtrar(matriz.jugadores || [])

  const jugadoresDelGrupo = (grupoId) => jugadoresAsignables
    .filter((j) => (j.jugadores_grupos || []).some((jg) => jg.grupo_id === grupoId && jg.activo && !jg.deleted))
    .map((j) => j.id)

  const jugadorEnGrupo = (j, grupoId) => (j.jugadores_grupos || []).some((jg) => jg.grupo_id === grupoId && jg.activo && !jg.deleted)

  // Lista de extras asignadas (una fila por asignación, origen='extra')
  const extrasList = (matriz.extras || [])
    .map((a) => ({ asignacion: a, jugador: jugadorById[a.jugador_id], prenda: prendaById[a.prenda_id] }))
    .filter((r) => r.jugador && r.prenda)
    .filter((r) => !inscritosEstado.filtrando || inscritosEstado.esInscrito(r.jugador.id))
    .filter((r) => {
      if (searchTerm) {
        const t = searchTerm.toLowerCase()
        if (!r.jugador.nombre_completo?.toLowerCase().includes(t) && !String(r.jugador.numero || '').includes(t)) return false
      }
      if (deporteFilter !== 'todos' && r.prenda.deporte !== 'todos' && r.prenda.deporte !== deporteFilter) return false
      if (grupoFilter !== 'todos' && !jugadorEnGrupo(r.jugador, grupoFilter)) return false
      if (pagadoFilter !== 'todos') {
        const ok = pagadoFilter === 'pagado' ? r.asignacion.pagado : !r.asignacion.pagado
        if (!ok) return false
      }
      if (entregaFilter !== 'todos' && (r.asignacion.estatus || 'no_entregado') !== entregaFilter) return false
      return true
    })
    .sort((a, b) => (a.jugador.nombre_completo || '').localeCompare(b.jugador.nombre_completo || ''))

  const prendaOptions = (matriz.prendas || []).map((p) => ({ value: p.id, label: `${p.nombre}${p.precio ? ` · ${formatMoney(p.precio)}` : ''}` }))
  const jugadorOptions = jugadoresAsignables.map((j) => ({ value: j.id, label: `${j.nombre_completo}${j.numero ? ` (#${j.numero})` : ''}` }))
  const grupoOptions = grupos.map((g) => ({ value: g.id, label: g.nombre_del_grupo }))
  const assignPrenda = (matriz.prendas || []).find((p) => p.id === assignForm.prendaId)

  const openAssign = () => { setAssignForm({ prendaId: '', tipo: 'jugador', jugadorId: '', grupoId: '', talla: '', cantidad: 1 }); assignGuard.resetDirty(); setAssignOpen(true) }

  const handleAssign = async () => {
    const { prendaId, tipo, jugadorId, grupoId, talla, cantidad } = assignForm
    if (!prendaId || !talla) return
    const qty = Math.max(1, Math.floor(Number(cantidad) || 1))
    setAssignSaving(true)
    let res
    if (tipo === 'grupo') {
      const ids = jugadoresDelGrupo(grupoId)
      if (!ids.length) { setAssignSaving(false); await alert('Sin jugadores', 'Ese grupo no tiene jugadores activos.'); return }
      res = await asignarPrendaMasivo({ jugadorIds: ids, prendaId, tallas: talla, temporadaId, cantidad: qty, origen: 'extra' })
    } else {
      if (!jugadorId) { setAssignSaving(false); return }
      res = await asignarPrenda({ jugadorId, prendaId, talla, temporadaId, cantidad: qty, origen: 'extra' })
    }
    setAssignSaving(false)
    if (res?.error) { await alert('Error', res.error.message || 'No se pudo asignar'); return }
    assignGuard.resetDirty()
    setAssignOpen(false)
    load()
  }

  const openEdit = (row) => {
    setEditModal({ open: true, row })
    setEditForm({ talla: row.asignacion.talla || '', estatus: row.asignacion.estatus || 'no_entregado' })
    editGuard.resetDirty()
  }
  const handleEditSave = async () => {
    if (!editForm.talla || !editModal.row) return
    setEditSaving(true)
    const { error } = await actualizarAsignacion(editModal.row.asignacion.id, { talla: editForm.talla, estatus: editForm.estatus })
    setEditSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo guardar'); return }
    editGuard.resetDirty()
    setEditModal({ open: false, row: null })
    load()
  }
  const handleEditDelete = async () => {
    if (!editModal.row) return
    if (!await confirm('Quitar prenda extra', 'Se cancelará el cargo de esta prenda si existe. ¿Continuar?', { destructive: true })) return
    setEditSaving(true)
    const { error } = await eliminarAsignacion(editModal.row.asignacion.id)
    setEditSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo eliminar'); return }
    editGuard.resetDirty()
    setEditModal({ open: false, row: null })
    load()
  }

  const assignReady = assignForm.prendaId && assignForm.talla && (assignForm.tipo === 'jugador' ? assignForm.jugadorId : assignForm.grupoId)
  const hayFiltros = deporteFilter !== 'todos' || grupoFilter !== 'todos' || pagadoFilter !== 'todos' || entregaFilter !== 'todos'

  return (
    <FeaturedLayout
      title="Extras"
      subtitle="Lo que se vende aparte del plan: un segundo jersey, una playera de porra. Al asignarla se le genera su cargo."
      refreshAction={load}
    >
      <div className="mb-3 space-y-1.5">
        <Label className="text-xs text-muted-foreground">Temporada</Label>
        <ChipFilter
          ariaLabel="Temporada"
          scroll
          opciones={chipsTemporada}
          valor={temporadaId || ''}
          onChange={(v) => v && setTemporadaId(v)}
        />
      </div>
      <div className="mb-3">
        <FiltroInscritos estado={inscritosEstado} id="ex-ver-no-inscritos" />
      </div>
      {inscritosEstado.sinPadron && (
        <div className="mb-3"><AvisoSinPadron estado={inscritosEstado} /></div>
      )}

      <div className="mb-4 flex items-center gap-2 flex-nowrap">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar jugador..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Button variant="outline" onClick={() => setShowFilters((s) => !s)} className="h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3 shrink-0">
          <Filter className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Filtros</span>
          {showFilters ? <ChevronUp className="ml-1 hidden h-4 w-4 sm:inline" /> : <ChevronDown className="ml-1 hidden h-4 w-4 sm:inline" />}
        </Button>
        <Button onClick={openAssign} className="h-10 shrink-0" aria-label="Asignar prenda extra" title="Asignar prenda extra">
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Asignar extra</span>
        </Button>
      </div>

      {showFilters && (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label className="text-xs">Deporte</Label>
            <Select value={deporteFilter} onValueChange={setDeporteFilter}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{deporteOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Grupo</Label>
            <Select value={grupoFilter} onValueChange={setGrupoFilter}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {grupos.map((g) => <SelectItem key={g.id} value={g.id}>{g.nombre_del_grupo}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Pago</Label>
            <Select value={pagadoFilter} onValueChange={setPagadoFilter}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="pagado">Pagado</SelectItem>
                <SelectItem value="no_pagado">Sin pagar</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Entrega</Label>
            <Select value={entregaFilter} onValueChange={setEntregaFilter}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="no_entregado">No entregado</SelectItem>
                <SelectItem value="pendiente">Pendiente</SelectItem>
                <SelectItem value="entregado">Entregado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {hayFiltros && (
            <div className="sm:col-span-2 lg:col-span-4">
              <Button variant="ghost" size="sm" onClick={() => { setDeporteFilter('todos'); setGrupoFilter('todos'); setPagadoFilter('todos'); setEntregaFilter('todos') }}>Limpiar filtros</Button>
            </div>
          )}
        </div>
      )}

      {/* Lista de extras */}
      {loading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
      ) : extrasList.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Package className="mx-auto mb-3 h-12 w-12 opacity-30" />
            <p>{hayFiltros || searchTerm ? 'Ninguna prenda extra coincide con los filtros.' : 'Aún no has vendido ninguna prenda aparte.'}</p>
            {!hayFiltros && !searchTerm && (
              <p className="mx-auto mt-2 max-w-md text-xs">
                Aquí se registra lo que un jugador compra fuera de su plan: un segundo
                jersey, una playera de porra, una chamarra. Se le genera su cargo al
                momento, y la prenda sigue el mismo camino que las demás: entra a un
                pedido y se reparte.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Jugador</TableHead>
                <TableHead>Prenda</TableHead>
                <TableHead className="text-center">Cant.</TableHead>
                <TableHead>Talla</TableHead>
                <TableHead className="text-right">Importe</TableHead>
                <TableHead>Pago</TableHead>
                <TableHead>Entrega</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {extrasList.map((r) => {
                const eb = ENTREGA_BADGE[r.asignacion.estatus || 'no_entregado'] || ENTREGA_BADGE.no_entregado
                return (
                  <TableRow key={r.asignacion.id} className="cursor-pointer" onClick={() => openEdit(r)}>
                    <TableCell className="font-medium">
                      {r.jugador.nombre_completo}
                      {r.jugador.numero && <Badge variant="outline" className="ml-2 text-[10px]">#{r.jugador.numero}</Badge>}
                    </TableCell>
                    <TableCell>{r.prenda.nombre}</TableCell>
                    <TableCell className="text-center tabular-nums">{r.asignacion.cantidad || 1}</TableCell>
                    <TableCell>{r.asignacion.talla || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatMoney((r.asignacion.cantidad || 1) * Number(r.prenda.precio || 0))}</TableCell>
                    <TableCell>
                      <Badge variant={r.asignacion.pagado ? 'success' : 'warning'}>
                        {r.asignacion.pagado ? 'Pagado' : 'Sin pagar'}
                      </Badge>
                    </TableCell>
                    <TableCell><Badge variant={eb.variant}>{eb.label}</Badge></TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Modal: asignar prenda extra */}
      <Dialog open={assignOpen} onOpenChange={assignGuard.onOpenChange}>
        <DialogContent {...assignGuard.dirtyProps} className="sm:max-w-md">
          <DialogHeader><DialogTitle>Asignar prenda extra</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">¿Qué prenda?</Label>
              <Combobox className="w-full" options={prendaOptions} value={assignForm.prendaId}
                onValueChange={(v) => { assignGuard.markDirty(); setAssignForm((p) => ({ ...p, prendaId: v, talla: '' })) }} searchPlaceholder="Buscar prenda..." placeholder="Elige una prenda" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">¿A quién?</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant={assignForm.tipo === 'jugador' ? 'default' : 'outline'} size="sm" onClick={() => { assignGuard.markDirty(); setAssignForm((p) => ({ ...p, tipo: 'jugador' })) }}>
                  <User className="mr-1 h-4 w-4" /> Un jugador
                </Button>
                <Button type="button" variant={assignForm.tipo === 'grupo' ? 'default' : 'outline'} size="sm" onClick={() => { assignGuard.markDirty(); setAssignForm((p) => ({ ...p, tipo: 'grupo' })) }}>
                  <Users className="mr-1 h-4 w-4" /> Un grupo
                </Button>
              </div>
            </div>

            {assignForm.tipo === 'jugador' ? (
              <div className="space-y-1.5">
                <Label className="text-xs">Jugador</Label>
                <Combobox className="w-full" options={jugadorOptions} value={assignForm.jugadorId}
                  onValueChange={(v) => { assignGuard.markDirty(); setAssignForm((p) => ({ ...p, jugadorId: v })) }} searchPlaceholder="Buscar jugador..." placeholder="Elige un jugador" />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">Grupo</Label>
                <Combobox className="w-full" options={grupoOptions} value={assignForm.grupoId}
                  onValueChange={(v) => { assignGuard.markDirty(); setAssignForm((p) => ({ ...p, grupoId: v })) }} searchPlaceholder="Buscar grupo..." placeholder="Elige un grupo" />
                {assignForm.grupoId && (
                  <p className="text-[11px] text-muted-foreground">Se asignará a {jugadoresDelGrupo(assignForm.grupoId).length} jugador(es) del grupo.</p>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Cantidad</Label>
              <Input className="h-9 w-28" type="number" min="1" value={assignForm.cantidad}
                onChange={(e) => setAssignForm((p) => ({ ...p, cantidad: e.target.value }))} />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Talla</Label>
              {!assignPrenda ? (
                <p className="text-xs text-muted-foreground">Elige una prenda primero.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(assignPrenda.tallas || []).map((t) => (
                    <button key={t} type="button" onClick={() => { assignGuard.markDirty(); setAssignForm((p) => ({ ...p, talla: t })) }}
                      aria-pressed={assignForm.talla === t}
                      className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${assignForm.talla === t ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background text-muted-foreground hover:bg-muted'}`}>
                      {t}
                    </button>
                  ))}
                  {(assignPrenda.tallas || []).length === 0 && <span className="text-xs text-muted-foreground">Esta prenda no tiene tallas configuradas.</span>}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => assignGuard.onOpenChange(false)}>Cancelar</Button>
            <Button onClick={handleAssign} disabled={assignSaving || !assignReady}>{assignSaving ? 'Asignando...' : 'Asignar'}</Button>
          </DialogFooter>
          {assignGuard.DiscardDialog}
        </DialogContent>
      </Dialog>

      {/* Modal: editar extra existente */}
      <Dialog open={editModal.open} onOpenChange={editGuard.onOpenChange}>
        <DialogContent {...editGuard.dirtyProps} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{editModal.row?.prenda?.nombre} — {editModal.row?.jugador?.nombre_completo}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Talla</Label>
              <div className="flex flex-wrap gap-2">
                {(editModal.row?.prenda?.tallas || []).map((t) => (
                  <button key={t} type="button" onClick={() => { editGuard.markDirty(); setEditForm((p) => ({ ...p, talla: t })) }}
                    aria-pressed={editForm.talla === t}
                    className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${editForm.talla === t ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background text-muted-foreground hover:bg-muted'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Entrega</Label>
              <Select value={editForm.estatus} onValueChange={(v) => { editGuard.markDirty(); setEditForm((p) => ({ ...p, estatus: v })) }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_entregado">No entregado</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="entregado">Entregado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <Button variant="ghost" size="sm" className="text-destructive" onClick={handleEditDelete} disabled={editSaving}>
              <Trash2 className="mr-1 h-4 w-4" /> Quitar
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => editGuard.onOpenChange(false)}>Cancelar</Button>
              <Button onClick={handleEditSave} disabled={editSaving || !editForm.talla}>{editSaving ? 'Guardando...' : 'Guardar'}</Button>
            </div>
          </DialogFooter>
          {editGuard.DiscardDialog}
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default ExtrasPage
